import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Building2, Home, Layers } from "lucide-react";
import { RATES, ADDONS } from "../lib/constants";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

function RateRow({ label, value, accent = false, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-4 border-b border-white/10 last:border-b-0">
      <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist">{label}</span>
      <span
        data-testid={testid}
        className={`font-display text-4xl sm:text-5xl leading-none ${accent ? "text-crystal" : "text-white"}`}
      >
        ${value}
      </span>
    </div>
  );
}

function ResidentialCard({ title, note, rates, featured, onBook, preset }) {
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-3xl p-8 sm:p-10 h-full flex flex-col transition-transform duration-200 hover:-translate-y-1 ${
        featured
          ? "bg-crystal/[0.06] border border-crystal/40"
          : "glass-panel"
      }`}
      style={featured ? { boxShadow: "0 0 80px -30px rgba(0,229,255,0.45)" } : {}}
      data-testid={`pricing-card-${preset.serviceKey}-${preset.scope}`}
    >
      {featured && (
        <span className="absolute -top-3 left-8 rounded-full bg-crystal px-4 py-1 font-mono text-[10px] font-semibold tracking-[0.25em] uppercase text-midnight">
          Most complete
        </span>
      )}
      <div className="flex items-center gap-3 mb-6">
        {featured ? <Layers size={18} className="text-crystal" /> : <Home size={18} className="text-mist" />}
        <h3 className="font-display text-3xl text-white">{title}</h3>
      </div>
      <p className="text-mist text-sm leading-relaxed mb-8">{note}</p>
      <div className="mt-auto">
        <RateRow label="Per pane" value={rates.pane} accent={featured} testid={`rate-${preset.serviceKey}-${preset.scope}-pane`} />
        <RateRow label="Per window" value={rates.window} accent={featured} testid={`rate-${preset.serviceKey}-${preset.scope}-window`} />
        <button
          data-testid={`book-${preset.serviceKey}-${preset.scope}-btn`}
          onClick={() => onBook(preset)}
          className="mt-8 w-full flex items-center justify-center gap-2 rounded-full border border-white/20 py-4 font-mono text-[11px] tracking-[0.25em] uppercase text-white hover:bg-crystal hover:text-midnight hover:border-crystal transition-colors duration-200"
        >
          Book this <ArrowUpRight size={14} />
        </button>
        <p className="mt-4 font-mono text-[10px] tracking-[0.15em] uppercase text-mist/70" data-testid={`example-total-${preset.serviceKey}-${preset.scope}`}>
          Example — 20 panes: <span className="text-white">${20 * rates.pane}</span>
        </p>
      </div>
    </motion.div>
  );
}

export default function Pricing({ onBook }) {
  const [story, setStory] = useState("res_2story");
  const storyRates = RATES[story];

  return (
    <section id="pricing" className="relative py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="max-w-3xl">
          <span className="block font-display italic text-7xl sm:text-8xl leading-none text-outline select-none mb-4" aria-hidden="true">01</span>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-9 h-[1px] bg-crystal" />
            <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-crystal">The Pricing</span>
          </div>
          <h2 className="font-display font-medium text-[clamp(2.2rem,5vw,4.2rem)] leading-[1.02] text-white tracking-tight">
            Naperville rates,
            <br />
            <em className="italic text-crystal">set in glass.</em>
          </h2>
          <p className="mt-6 text-mist leading-relaxed">
            Priced at the top of the Naperville market — the highest standard
            of clean, with your exact total calculated before you book.
          </p>
          <p className="mt-3 inline-flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-ink/60 px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-mist">
              <span className="w-1.5 h-1.5 rotate-45 bg-crystal" />
              One window = one frame · a double-hung counts as two panes
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-crystal/40 bg-crystal/[0.07] px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-crystal" data-testid="plans-chip">
              <span className="w-1.5 h-1.5 rotate-45 bg-crystal" />
              Plans: 3, 6 or 12 months · website price locked in · one-time always welcome
            </span>
          </p>
        </motion.div>

        {/* Residential */}
        <div className="mt-14">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h3 className="font-mono text-xs tracking-[0.3em] uppercase text-mist">Residential — 1 &amp; 2 story homes</h3>
            <div className="flex rounded-full border border-white/15 p-1 bg-ink/70" data-testid="story-toggle">
              {Object.values(RATES).filter((r) => r.kind === "residential").map((r) => (
                <button
                  key={r.key}
                  data-testid={`story-toggle-${r.key}`}
                  onClick={() => setStory(r.key)}
                  className={`rounded-full px-6 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors duration-200 ${
                    story === r.key ? "bg-crystal text-midnight font-semibold" : "text-mist hover:text-white"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <ResidentialCard
              key={`${story}-exterior`}
              title="Exterior Only"
              note="Every outside pane, squeegee-detailed and left invisible. Two-story ladder work included."
              rates={{ pane: storyRates.pane.exterior, window: storyRates.window.exterior }}
              featured={false}
              onBook={onBook}
              preset={{ serviceKey: story, scope: "exterior" }}
            />
            <ResidentialCard
              key={`${story}-inside`}
              title="Inside & Out"
              note="The full pass — exterior and interior glass, panel by panel, sills detailed."
              rates={{ pane: storyRates.pane.inside_out, window: storyRates.window.inside_out }}
              featured
              onBook={onBook}
              preset={{ serviceKey: story, scope: "inside_out" }}
            />
          </div>
        </div>

        {/* Storefront */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-6 lg:mt-8 rounded-3xl overflow-hidden glass-panel"
          data-testid="pricing-card-storefront"
        >
          <div className="grid lg:grid-cols-[1.2fr_1fr_1fr_auto] items-center gap-8 p-8 sm:p-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Building2 size={18} className="text-crystal" />
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-crystal">Commercial</span>
              </div>
              <h3 className="font-display text-3xl sm:text-4xl text-white">Storefronts — always inside &amp; out</h3>
              <p className="text-mist text-sm mt-3 leading-relaxed">
                Your glass is your first impression. Interior and exterior
                panes, detailed before your doors open. Weekly, bi-weekly or
                monthly plans — 3, 6 or 12 months, same price per visit.
              </p>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-white/10 lg:pl-8 pt-6 lg:pt-0">
              <RateRow label="Per pane" value={RATES.storefront.pane.inside_out} accent testid="rate-storefront-pane" />
              <RateRow label="Per window" value={RATES.storefront.window.inside_out} accent testid="rate-storefront-window" />
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-white/10 lg:pl-8 pt-6 lg:pt-0">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist mb-3">Visit minimum</div>
              <div className="font-display text-5xl text-white" data-testid="rate-storefront-minimum">$75</div>
            </div>
            <div className="lg:pr-2">
              <button
                data-testid="book-storefront-btn"
                onClick={() => onBook({ serviceKey: "storefront", scope: "inside_out" })}
                className="w-full lg:w-auto flex items-center justify-center gap-2 rounded-full bg-crystal px-8 py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase text-midnight transition-transform duration-200 hover:-translate-y-0.5 whitespace-nowrap"
              >
                Book storefront <ArrowUpRight size={14} />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Around the glass — priced add-ons */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-6 lg:mt-8 rounded-3xl glass-panel p-8 sm:p-10"
          data-testid="addons-panel"
        >
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <h3 className="font-display text-3xl sm:text-4xl text-white">Around the glass — priced, not guessed.</h3>
              <p className="text-mist text-sm mt-2">
                Trims, ledges, screens, tracks — every detail around your
                windows, itemized flat so the total never surprises you.
              </p>
            </div>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-crystal">Add when you book</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-0">
            {ADDONS.map((a) => (
              <div key={a.key} className="flex items-baseline justify-between gap-4 py-4 border-b border-white/10" data-testid={`price-addon-${a.key}`}>
                <div>
                  <div className="text-sm text-white">{a.label}</div>
                  {a.desc && <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-mist mt-0.5">{a.desc}</div>}
                </div>
                <div className="font-display text-3xl text-crystal shrink-0">${a.price}<span className="text-mist text-sm"> {a.unit}</span></div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.p {...fadeUp} transition={{ duration: 0.6 }} className="mt-8 font-mono text-[11px] tracking-[0.15em] uppercase text-mist/70">
          * Top-of-market Naperville rates for standard reachable glass. Heights beyond two stories &amp; tricky access quoted on site — always before we start.
        </motion.p>
      </div>
    </section>
  );
}
